import { type AbsenceDeputyState, clampDateTo, type CalendarEntryDto, type ConflictDto, type MeDto } from '@bqcrm/shared';
import { Alert, Badge, Button, Group, Modal, Stack, Text, TextInput } from '@mantine/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../../api';
import { notifyError, notifyOk } from '../../notify';
import { AbsenceDeputyFields, deputyBody, deputyIncomplete, type DeputyValue } from './AbsenceDeputyFields';
import { ConflictList } from './common';

const STATE: Record<AbsenceDeputyState, { label: string; color: string }> = {
  czeka: { label: 'po zatwierdzeniu wniosku', color: 'yellow' },
  zaplanowane: { label: 'zaplanowane', color: 'blue' },
  trwa: { label: 'trwa', color: 'green' },
  zakonczone: { label: 'zakończone', color: 'gray' },
};

/** Zastępca przy nieobecności w oknie wpisu: „Zastępuje: Jan Kowalski · trwa”. */
export function DeputyInfo({ entry }: { entry: CalendarEntryDto }) {
  if (!entry.deputy) return null;
  const s = STATE[entry.deputy.state];
  return (
    <Group gap="xs">
      <Text size="sm">
        Zastępuje: <b>{entry.deputy.name}</b>
      </Text>
      <Badge size="sm" variant="light" color={s.color}>
        zastępstwo {s.label}
      </Badge>
      {entry.deputy.notifyPending && (
        <Text size="xs" c="dimmed">
          e-mail do klientów po zatwierdzeniu
        </Text>
      )}
    </Group>
  );
}

/** Zmiana dat nieobecności i osoby zastępującej — zastępstwo idzie za nieobecnością (docs/16). */
export function AbsenceEditDialog({ me, entry, onClose }: { me: MeDto; entry: CalendarEntryDto; onClose: () => void }) {
  const qc = useQueryClient();
  const personId = entry.userIds[0] ?? me.id;
  const [dates, setDates] = useState({ dateFrom: entry.dateFrom, dateTo: entry.dateTo });
  const [deputy, setDeputy] = useState<DeputyValue>({ deputyId: entry.deputy?.id ?? null, notifyClients: !!entry.deputy?.notifyPending, caseIds: null });
  const [conflicts, setConflicts] = useState<ConflictDto[] | null>(null);
  const save = useMutation({
    mutationFn: () =>
      api<{ entry: CalendarEntryDto; conflicts: ConflictDto[] }>(`/calendar/entries/${entry.id}/absence`, {
        method: 'PUT',
        body: { ...dates, dateTo: dates.dateTo < dates.dateFrom ? dates.dateFrom : dates.dateTo, ...(entry.can.setDeputy ? deputyBody(deputy) : { deputyId: entry.deputy?.id ?? null, notifyClients: !!entry.deputy?.notifyPending }) },
      }),
    onSuccess: (r) => {
      for (const k of ['calendar', 'planning-board', 'absences-pending', 'process-inbox']) void qc.invalidateQueries({ queryKey: [k] });
      notifyOk('Zapisano nieobecność');
      if (r.conflicts.length) setConflicts(r.conflicts);
      else onClose();
    },
    onError: notifyError,
  });
  return (
    <Modal opened onClose={onClose} title={`Zmiana: ${entry.title}`} size="lg">
      {conflicts ? (
        <Stack>
          <Alert color="orange" title="Nieobecność koliduje z zaplanowanymi zadaniami">
            <ConflictList conflicts={conflicts} />
          </Alert>
          <Group justify="flex-end">
            <Button onClick={onClose}>Zamknij</Button>
          </Group>
        </Stack>
      ) : (
        <Stack>
          {entry.can.editDates ? (
            <Group grow align="flex-end">
              <TextInput type="date" label="Od" value={dates.dateFrom} onChange={(e) => setDates({ dateFrom: e.currentTarget.value, dateTo: clampDateTo(e.currentTarget.value, dates.dateTo) })} />
              <TextInput type="date" label="Do" value={dates.dateTo} min={dates.dateFrom || undefined} onChange={(e) => setDates({ ...dates, dateTo: e.currentTarget.value })} />
            </Group>
          ) : (
            <Text size="sm" c="dimmed">
              Daty zmieni osoba z uprawnieniem „Zatwierdzanie nieobecności”.
            </Text>
          )}
          {entry.can.setDeputy && (
            <AbsenceDeputyFields me={me} personId={personId} value={deputy} onChange={setDeputy} hourly={!!entry.timeFrom} pending={entry.absenceStatus === 'wniosek'} />
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>
              Anuluj
            </Button>
            <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!dates.dateFrom || !dates.dateTo || deputyIncomplete(deputy)}>
              Zapisz
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}
